/**
 * Frontend API client for the Market tab.
 * Maps 1:1 to the /v1/observe/dbview/market/* endpoints in
 * backend/product/src/api/server.ts.
 */

import { request } from './client';

// ── Types ───────────────────────────────────────────────────────────────────

export interface MarketHeadline {
  ok: true;
  sinceMs: number;
  soldListings: number;
  distinctModelKeys: number;
  cexListingsWithAnchor: number;
  topTradedModelKey: { modelKey: string; soldCount: number } | null;
  medianSoldVsCexSpreadPct: number | null;
}

export interface SeriesBucket {
  ts: string;
  ebayP25?: number;
  ebayP50?: number;
  ebayP75?: number;
  ebayCount?: number;
  cexCash?: number;
  cexVoucher?: number;
  cexCount?: number;
}

export interface SeriesResp {
  ok: true;
  modelKey: string;
  sinceMs: number;
  bucket: 'day' | 'week' | 'month';
  buckets: SeriesBucket[];
}

export interface SupplyBucket {
  ts: string;
  soldVelocity: number;
  liveListings: number;
}

export interface SupplyResp {
  ok: true;
  modelKey: string;
  sinceMs: number;
  bucket: 'day' | 'week' | 'month';
  buckets: SupplyBucket[];
}

export interface Mover {
  modelKey: string;
  currentMedian: number;
  priorMedian: number;
  deltaAbs: number;
  deltaPct: number;
  currentVolume: number;
}

export interface SpreadRow {
  modelKey: string;
  ebayMedian: number;
  cexCash: number;
  spreadAbs: number;
  spreadPct: number;
  ebayVolume: number;
}

export interface DistributionBin {
  rangeStart: number;
  rangeEnd: number;
  count: number;
}

export interface DistributionResp {
  ok: true;
  modelKey: string;
  bins: DistributionBin[];
  total: number;
}

// ── Fetchers ────────────────────────────────────────────────────────────────

function qs(params: object): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

export function getMarketHeadline(opts: { sinceMs?: number } = {}) {
  return request<MarketHeadline>(`/v1/observe/dbview/market/headline${qs(opts)}`);
}

export function searchMarketModelKeys(opts: { q?: string; limit?: number } = {}) {
  return request<{ ok: true; rows: string[] }>(`/v1/observe/dbview/market/modelKeys${qs(opts)}`);
}

export function listPopularModelKeys(opts: { sinceMs?: number; limit?: number } = {}) {
  return request<{ ok: true; rows: Array<{ modelKey: string; soldCount: number }> }>(
    `/v1/observe/dbview/market/modelKeys/popular${qs(opts)}`,
  );
}

export function getMarketSeries(opts: { modelKey: string; sinceMs?: number; bucket?: 'day' | 'week' | 'month' }) {
  return request<SeriesResp>(`/v1/observe/dbview/market/series${qs(opts)}`);
}

export function getMarketSeriesMulti(opts: { modelKeys: string[]; sinceMs?: number; bucket?: 'day' | 'week' | 'month' }) {
  return request<{ ok: true; rows: SeriesResp[] }>(
    `/v1/observe/dbview/market/series/multi${qs({ ...opts, modelKeys: opts.modelKeys.join(',') })}`,
  );
}

export function getMarketSupply(opts: { modelKey: string; sinceMs?: number; bucket?: 'day' | 'week' | 'month' }) {
  return request<SupplyResp>(`/v1/observe/dbview/market/supply${qs(opts)}`);
}

export function getMarketMovers(opts: { sinceMs?: number; limit?: number; sort?: 'up' | 'down' | 'abs' | 'volume' } = {}) {
  return request<{ ok: true; rows: Mover[] }>(`/v1/observe/dbview/market/movers${qs(opts)}`);
}

export function getMarketDistribution(opts: { modelKey: string; sinceMs?: number; condition?: string; binCount?: number }) {
  return request<DistributionResp>(`/v1/observe/dbview/market/distribution${qs(opts)}`);
}

export function getMarketSpread(opts: { sinceMs?: number; limit?: number; sort?: 'positive' | 'negative' | 'absolute' | 'volume' } = {}) {
  return request<{ ok: true; rows: SpreadRow[] }>(`/v1/observe/dbview/market/spread${qs(opts)}`);
}

/** Build the CSV export URL for the current selection. The CsvExportButton
 *  component fetches this with the auth header and triggers the download. */
export function marketExportPath(opts: { modelKey: string; sinceMs?: number; bucket?: 'day' | 'week' | 'month' }): string {
  return `/v1/observe/dbview/market/export${qs(opts)}`;
}
